import { parseTransactionIds } from "helpers/transactionIdFilter";
import moment from "moment";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";

import ActionBtn from "components/buttons/ActionBtn";
import Button from "components/buttons/Button";
import EmptyState from "components/EmptyState/EmptyState";
import Pagination from "components/Pagination";
import PaginationSkeleton from "components/global/Skeleton/PaginationSkeleton/PaginationSkeleton";
import DataTableSkeleton from "components/global/Skeleton/TableSkeleton/DataTableSkeleton";
import { Table } from "components/table/Table";
import {
  confiscationsListLimit,
  confiscationsListLimits,
} from "configs/limits";
import { ModalTypes } from "configs/types";
import { usePermissionHelper } from "helpers/usePermissionHelper";
import { useAppDispatch, useAppSelector } from "store";
import { openModal } from "store/modals/slice";
import { NotificationTypes, showNotification } from "store/notifications/slice";
import {
  ConfiscationOrder,
  getConfiscationsList,
  handleConfiscationsPaginate,
  resetConfiscationsList,
} from "store/payments/confiscations/slice";
import { initSelectPlayer } from "store/payments/selectPlayer/slice";
import ConfiscationsFilters, {
  ConfiscationFilterValues,
  FilterOption,
} from "./components/ConfiscationsFilters";
import { useRefreshList } from "helpers/useRefreshList";
import { RefreshButton } from "components/buttons/RefreshButton";

const TYPE_LABELS: Record<string, string> = {
  fund_confiscation: "Fund confiscation",
  other_debit: "Other debit",
  balance_transfer_debit: "Balance transfer debit",
};

const STATUS_LABELS: Record<string, string> = {
  completed: "Completed",
  pending: "Pending",
  failed: "Failed",
  created: "Created",
  processing: "Processing",
  declined: "Declined",
  canceled: "Canceled",
};

const displayValue = (value?: string | number | null) =>
  value === undefined || value === null || value === "" ? "—" : value;

const EMPTY_FILTERS: ConfiscationFilterValues = {
  userIds: "",
  playerNickname: "",
  transactionCode: "",
  admins: [],
  types: [],
  currencies: [],
  amountFrom: "",
  amountTo: "",
  createdFrom: "",
  createdTo: "",
};

const toUtcIso = (value: string) =>
  moment(value, "DD.MM.YYYY HH:mm", true).toISOString();

const ConfiscationsList: React.FC = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const permissions = usePermissionHelper();
  const canRead = permissions?.read ?? false;
  const canCreate = permissions?.create ?? false;

  const [order, setOrder] = useState<ConfiscationOrder>({ id: "desc" });
  const [currentLimit, setCurrentLimit] = useState(confiscationsListLimit);
  const [draftFilters, setDraftFilters] =
    useState<ConfiscationFilterValues>(EMPTY_FILTERS);
  const [appliedFilters, setAppliedFilters] =
    useState<ConfiscationFilterValues>(EMPTY_FILTERS);

  const {
    list,
    activePage,
    totalPages,
    totalItems,
    isListFetched,
    hasListError,
  } = useAppSelector((store) => store.paymentsConfiscations);

  const { refresh, isRefreshRequested } = useRefreshList({
    sliceName: 'paymentsConfiscations',
    isListFetched
  });

  const fetchList = useCallback(() => {
    if (!canRead) return;

    const userIds = parseTransactionIds(appliedFilters.userIds);

    const operatorIds = appliedFilters.admins
      .map((item) => Number(item.value))
      .filter(Number.isFinite);

    const amountFrom = appliedFilters.amountFrom.trim();
    const amountTo = appliedFilters.amountTo.trim();
    const currencies = appliedFilters.currencies.map((item) =>
      String(item.value),
    );

    dispatch(
      getConfiscationsList({
        limit: currentLimit,
        offset: activePage - 1,
        order,
        ...(userIds.length && { userIds }),
        ...(appliedFilters.playerNickname.trim() && {
          playerNickname: appliedFilters.playerNickname.trim(),
        }),
        ...(operatorIds.length && { operatorIds }),
        ...(appliedFilters.types.length && {
          confiscationTypes: appliedFilters.types.map((item) =>
            String(item.value),
          ),
        }),
        ...(appliedFilters.transactionCode && {
          transactionCode: appliedFilters.transactionCode,
        }),
        ...(currencies.length && { currencies }),
        ...(amountFrom && { amountFrom }),
        ...(amountTo && { amountTo }),
        ...(appliedFilters.createdFrom && {
          createdFrom: toUtcIso(appliedFilters.createdFrom),
        }),
        ...(appliedFilters.createdTo && {
          createdTo: toUtcIso(appliedFilters.createdTo),
        }),
      }),
    );
  }, [activePage, appliedFilters, canRead, currentLimit, dispatch, order]);

  useEffect(() => {
    if (!isListFetched) fetchList();
  }, [fetchList, isListFetched]);

  useEffect(() => {
    if (hasListError) {
      dispatch(
        showNotification({
          title: t("error"),
          message: t("confiscationsListError"),
          type: NotificationTypes.error,
        }),
      );
    }
  }, [dispatch, hasListError, t]);

  useEffect(
    () => () => {
      dispatch(resetConfiscationsList());
    },
    [dispatch],
  );

  const handleOrderClick = () => {
    setOrder((current) => ({ id: current.id === "desc" ? "asc" : "desc" }));
    dispatch(handleConfiscationsPaginate(1));
  };

  const handleOpenSelectPlayer = () => {
    if (!canCreate) return;

    dispatch(initSelectPlayer({ purpose: "confiscation", reset: true }));
    navigate(
      `${location.pathname}?activeModal=${ModalTypes.selectPlayer}&purpose=confiscation`,
    );
    dispatch(openModal({ activeModal: ModalTypes.selectPlayer }));
  };

  const updateDraft = <K extends keyof ConfiscationFilterValues>(
    key: K,
    value: ConfiscationFilterValues[K],
  ) => setDraftFilters((current) => ({ ...current, [key]: value }));

  const handleApply = useCallback(() => {
    if (!isListFetched) return;

    if (
      draftFilters.createdFrom &&
      draftFilters.createdTo &&
      moment(draftFilters.createdFrom, "DD.MM.YYYY HH:mm", true).isAfter(
        moment(draftFilters.createdTo, "DD.MM.YYYY HH:mm", true),
      )
    ) {
      dispatch(
        showNotification({
          title: t("error"),
          message: "Created From must not be later than Created To.",
          type: NotificationTypes.error,
        }),
      );
      return;
    }

    const amountFrom = draftFilters.amountFrom.trim();
    const amountTo = draftFilters.amountTo.trim();

    if ((amountFrom || amountTo) && draftFilters.currencies.length !== 1) {
      dispatch(
        showNotification({
          title: t("error"),
          message: t("confiscationAmountCurrencyRequired"),
          type: NotificationTypes.error,
        }),
      );
      return;
    }

    setAppliedFilters({
      ...draftFilters,
      playerNickname: draftFilters.playerNickname.trim(),
      transactionCode: draftFilters.transactionCode.trim(),
      amountFrom,
      amountTo,
    });
    dispatch(handleConfiscationsPaginate(1));
  }, [dispatch, draftFilters, isListFetched, t]);

  const handleReset = useCallback(() => {
    setDraftFilters(EMPTY_FILTERS);
    setAppliedFilters(EMPTY_FILTERS);
    dispatch(handleConfiscationsPaginate(1));
  }, [dispatch]);

  const operatorOptions = useMemo(() => {
    const options = new Map<number, FilterOption>();
    list.forEach((item) => {
      if (item.operatorId != null && item.operatorName) {
        options.set(item.operatorId, {
          value: item.operatorId,
          label: item.operatorName,
        });
      }
    });
    [...appliedFilters.admins, ...draftFilters.admins].forEach((operator) =>
      options.set(Number(operator.value), operator),
    );
    return Array.from(options.values());
  }, [list, appliedFilters.admins, draftFilters.admins]);

  const tableData = list.map((item) => ({
    operationId: item.operationId,
    userId: item.userId,
    playerNickname: displayValue(item.playerNickname),
    playerEmail: displayValue(item.playerEmail),
    type: item.confiscationType
      ? (TYPE_LABELS[item.confiscationType] ?? item.confiscationType)
      : "—",
    amount: `${item.amount} ${item.currency}`,
    transactionCode: displayValue(item.transactionCode),
    admin: displayValue(item.operatorName),
    status: item.statusCode
      ? (STATUS_LABELS[item.statusCode] ?? item.statusCode)
      : "—",
    created: item.createdAt,
  }));

  if (!canRead) return null;

  return (
    <div className="page-list-wrapper page-list--with-filter">
      <ConfiscationsFilters
        {...draftFilters}
        operatorOptions={operatorOptions}
        disabled={!isListFetched}
        setUserIds={(value) => updateDraft("userIds", value)}
        setPlayerNickname={(value) => updateDraft("playerNickname", value)}
        setTransactionCode={(value) => updateDraft("transactionCode", value)}
        setAdmins={(value: FilterOption[]) => updateDraft("admins", value)}
        setTypes={(value: FilterOption[]) => updateDraft("types", value)}
        setCurrencies={(value: FilterOption[]) =>
          updateDraft("currencies", value)
        }
        setAmountFrom={(value) => updateDraft("amountFrom", value)}
        setAmountTo={(value) => updateDraft("amountTo", value)}
        setCreatedRange={(from, to) =>
          setDraftFilters((current) => ({
            ...current,
            createdFrom: from,
            createdTo: to,
          }))
        }
        onApply={handleApply}
        onReset={handleReset}
      />

      <div className="page-list--with-filter-content">
        <div className="page-list-header">
          {canCreate && (
            <div className="page-list-header--box">
              <ActionBtn
                txt={t("createConfiscation")}
                handleClick={handleOpenSelectPlayer}
              />
            </div>
          )}
          <div className="page-list-header--box page-list-header--box--right">
            <RefreshButton
              position="right"
              disabled={isRefreshRequested}
              isLoading={isRefreshRequested}
              onClick={() => refresh()}
            />
          </div>
        </div>

        {!isListFetched && (
          <>
            <DataTableSkeleton columnsCount={10} rowsCount={12} />
            <PaginationSkeleton />
          </>
        )}

        {isListFetched && hasListError && (
          <div className="game-table-wrap">
            <EmptyState message={t("confiscationsListError")} />
            <Button isGreen txt={t("retry")} handleClick={fetchList} />
          </div>
        )}

        {isListFetched && !hasListError && (
          <div className="game-table-wrap table">
            {list.length === 0 && (
              <EmptyState message={t("noConfiscationTransactionsFound")} />
            )}

            {list.length > 0 && (
              <Table
                data={tableData}
                idField="operationId"
                hasCheckbox={false}
                hasOptions={false}
                onSort={(key) => {
                  if (key === "operationId") handleOrderClick();
                }}
                columns={{
                  sortingColumns: {
                    operationId: {
                      value: "ID",
                      hasSorting: true,
                      sortedBy: order.id,
                    },
                    userId: { value: "User ID" },
                    playerNickname: { value: "Player Nickname" },
                    playerEmail: { value: "Player Email" },
                    type: { value: "Type" },
                    amount: { value: "Amount" },
                    transactionCode: { value: "Transaction Code" },
                    admin: { value: "Admin" },
                    status: { value: "Status" },
                    created: { value: "Created" },
                  },
                }}
              />
            )}
          </div>
        )}

        {isListFetched && !hasListError && list.length > 0 && (
          <Pagination
            limitsArray={confiscationsListLimits}
            handleLimitClick={(limit) => {
              setCurrentLimit(limit);
              dispatch(handleConfiscationsPaginate(1));
            }}
            activePage={activePage}
            limit={currentLimit}
            totalItems={totalItems}
            totalPages={totalPages}
            handlePrevClick={() => {
              if (activePage > 1) {
                dispatch(handleConfiscationsPaginate(activePage - 1));
              }
            }}
            handleNextClick={() => {
              if (activePage < totalPages) {
                dispatch(handleConfiscationsPaginate(activePage + 1));
              }
            }}
          />
        )}
      </div>
    </div>
  );
};

export default ConfiscationsList;
